import type { ConsoleEvalCase, ConsoleTestAccount } from '@plato/shared';
import { useState } from 'react';
import { ActionError, useAction } from '../../components/action.tsx';
import { useConsole } from '../../data/console-context.tsx';

export function CaseForm(props: {
  existing: ConsoleEvalCase | null;
  accounts: readonly ConsoleTestAccount[];
  onDone: (saved: boolean) => void;
}) {
  const { data, tenant } = useConsole();
  const [name, setName] = useState(props.existing?.name ?? '');
  const [question, setQuestion] = useState(props.existing?.question ?? '');
  const [rubric, setRubric] = useState(props.existing?.rubric ?? '');
  const [notes, setNotes] = useState(props.existing?.reference_notes ?? '');
  const [account, setAccount] = useState(props.existing?.test_account_id ?? '');
  const action = useAction();

  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        const fields = {
          name: name.trim(),
          question: question.trim(),
          rubric,
          reference_notes: notes.trim() === '' ? null : notes,
          test_account_id: account === '' ? null : account,
        };
        void action
          .run(async () => {
            if (props.existing === null) await data.createEvalCase(tenant.id, fields);
            else await data.updateEvalCase(tenant.id, props.existing.id, fields);
          })
          .then((ok) => ok && props.onDone(true));
      }}
    >
      <h2>{props.existing === null ? 'New case' : `Edit “${props.existing.name}”`}</h2>
      <fieldset disabled={action.pending}>
        <label>
          Name
          <input value={name} maxLength={200} required onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Question
          <span className="hint">Asked exactly as a user of your product would ask it.</span>
          <textarea
            value={question}
            rows={3}
            maxLength={20_000}
            required
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>
        <label>
          Rubric
          <span className="hint">
            What a good answer must do, for the judge. Name things it can check in the answer: the
            number or fact it must give, the tool it must use, what it must refuse or ask first. One
            requirement per line works best. A case without a rubric is not run.
          </span>
          <textarea
            value={rubric}
            rows={5}
            maxLength={10_000}
            placeholder={
              'Gives the number of orders placed last week.\nSays which dates "last week" covers.\nDoes not guess when the orders tool fails.'
            }
            onChange={(e) => setRubric(e.target.value)}
          />
        </label>
        <label>
          Reference notes
          <span className="hint">Optional facts the judge may use, such as the right answer.</span>
          <textarea
            value={notes}
            rows={2}
            maxLength={10_000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <label>
          Test account
          <span className="hint">
            Who the assistant acts as. Without one it answers with no access to your product.
          </span>
          <select value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">None (no vendor tools)</option>
            {props.accounts.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
        <div className="row">
          <button type="submit" className="primary">
            Save case
          </button>
          <button type="button" className="quiet" onClick={() => props.onDone(false)}>
            Cancel
          </button>
        </div>
      </fieldset>
      <ActionError error={action.error} />
    </form>
  );
}
